/**
 * A planilha de categorias (F243): `codigo;categoria;subcategoria`.
 *
 * Lida no navegador, para a prévia sair sem subir arquivo: a API recebe as
 * linhas já separadas. Aceita o que o Excel e o Google Planilhas exportam em
 * CSV:
 *
 * - ponto e vírgula, vírgula ou tabulação, decidido pela primeira linha;
 * - campo entre aspas, com `""` dentro;
 * - o BOM do Excel no começo;
 * - o cabeçalho, quando a primeira célula é "código" ou "codigo".
 */

export type CategorySheetRow = {
  readonly codigo: string
  readonly categoria: string
  readonly subcategoria: string | null
}

export type CategorySheet = {
  readonly rows: readonly CategorySheetRow[]
  /** As linhas sem código ou sem categoria, pelo número da linha no arquivo. */
  readonly skippedLines: readonly number[]
}

const splitLine = (line: string, separator: string): string[] => {
  const cells: string[] = []
  let cell = ''
  let quoted = false
  for (let index = 0; index < line.length; index += 1) {
    const char = line.charAt(index)
    if (quoted) {
      if (char === '"' && line[index + 1] === '"') {
        cell += '"'
        index += 1
      } else if (char === '"') {
        quoted = false
      } else {
        cell += char
      }
    } else if (char === '"') {
      quoted = true
    } else if (char === separator) {
      cells.push(cell)
      cell = ''
    } else {
      cell += char
    }
  }
  cells.push(cell)
  return cells.map((value) => value.trim())
}

const separatorOf = (line: string): string => {
  const counts = [';', '\t', ','].map((separator) => ({
    separator,
    count: line.split(separator).length - 1,
  }))
  const best = counts.reduce((a, b) => (b.count > a.count ? b : a))
  return best.count === 0 ? ';' : best.separator
}

const isHeader = (cell: string): boolean =>
  ['codigo', 'código', 'cod', 'cód'].includes(cell.trim().toLowerCase())

export const parseCategorySheet = (text: string): CategorySheet => {
  const lines = text.replace(/^\u{FEFF}/u, '').split(/\r\n|\n|\r/)
  const first = lines.find((line) => line.trim() !== '') ?? ''
  const separator = separatorOf(first)

  const rows: CategorySheetRow[] = []
  const skippedLines: number[] = []
  lines.forEach((line, index) => {
    if (line.trim() === '') return
    const [codigo = '', categoria = '', subcategoria = ''] = splitLine(
      line,
      separator
    )
    if (rows.length === 0 && skippedLines.length === 0 && isHeader(codigo)) {
      return
    }
    if (codigo === '' || categoria === '') {
      skippedLines.push(index + 1)
      return
    }
    rows.push({
      codigo,
      categoria,
      subcategoria: subcategoria === '' ? null : subcategoria,
    })
  })

  return { rows, skippedLines }
}
